import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Icon } from "@iconify/react";
import { getAllStageQuestions, updateStageQuestion } from "../../Utils/apiServices";
import {
  IDEA_FLOW_STAGE_NAMES,
  getIdeaFlowStageName,
} from "../../Container/IdeasReview/utils";
import Button from "../Button";
import Table from "../Table";
import { Typography } from "../Typography";
import toast from "../../Utils/toastManager";

const extractStageQuestions = (response) => {
  if (!response?.success) return [];
  const data = response.data?.data ?? response.data;
  return Array.isArray(data) ? data : [];
};

const normalizeQuestionText = (text) =>
  String(text || "")
    .replace(/\s+/g, " ")
    .trim();

const getStageLabel = (record) => {
  const stageNumber = record?.stage_number;
  const name = getIdeaFlowStageName(stageNumber, { zeroBased: false });
  if (name) return name;
  if (stageNumber != null) return `Stage ${stageNumber}`;
  return "—";
};

const StageQuestionList = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [editingRecord, setEditingRecord] = useState(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllStageQuestions();
      const list = extractStageQuestions(response);
      list.sort((a, b) => {
        const stageA = Number(a?.stage_number) || 0;
        const stageB = Number(b?.stage_number) || 0;
        if (stageA !== stageB) return stageA - stageB;
        return Number(a.id) - Number(b.id);
      });
      setQuestions(list);
    } catch (error) {
      console.error("Error fetching stage questions:", error);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const filteredQuestions = useMemo(() => {
    let list = questions;

    if (stageFilter) {
      list = list.filter(
        (q) => getStageLabel(q).toLowerCase() === stageFilter.toLowerCase()
      );
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter((item) => {
        const question = normalizeQuestionText(item.question).toLowerCase();
        const stage = getStageLabel(item).toLowerCase();
        return (
          question.includes(q) ||
          stage.includes(q) ||
          String(item.id).includes(q) ||
          String(item.stage_number).includes(q)
        );
      });
    }

    return list;
  }, [questions, searchTerm, stageFilter]);

  const stageCounts = useMemo(() => {
    const counts = {};
    questions.forEach((item) => {
      const label = getStageLabel(item);
      counts[label] = (counts[label] || 0) + 1;
    });
    return counts;
  }, [questions]);

  const openEdit = (record) => {
    setEditingRecord(record);
    setEditQuestion(normalizeQuestionText(record.question));
  };

  const closeEdit = () => {
    setEditingRecord(null);
    setEditQuestion("");
  };

  const handleSave = async () => {
    if (!editingRecord) return;
    const trimmed = editQuestion.trim();
    if (!trimmed) {
      toast.warning("Question text cannot be empty.");
      return;
    }

    setSaving(true);
    try {
      const response = await updateStageQuestion(editingRecord.id, {
        id: String(editingRecord.id),
        stage_number: Number(editingRecord.stage_number),
        question: trimmed,
      });

      if (response?.success) {
        await fetchQuestions();
        closeEdit();
      } else {
        toast.error(response?.error || "Failed to update question.");
      }
    } catch (error) {
      console.error("Error updating stage question:", error);
      toast.error("An error occurred while updating the question.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-6 p-2">
        <div className="h-8 w-48 bg-slate-200 rounded-lg animate-pulse" />
        <div className="h-64 bg-slate-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Typography as="h2" className="text-2xl font-semibold text-primary-900">
            Questions
          </Typography>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
            {questions.length}
          </span>
        </div>
        <Button
          variant="secondary"
          size="medium"
          startIcon={<Icon icon="lucide:refresh-cw" className="h-4 w-4" />}
          onClick={fetchQuestions}
        >
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {IDEA_FLOW_STAGE_NAMES.map((name) => (
          <div
            key={name}
            className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm"
          >
            <Typography as="p" className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              {name}
            </Typography>
            <Typography as="p" className="text-xl font-bold text-slate-900 mt-1">
              {stageCounts[name] || 0}
            </Typography>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by question, stage, or ID..."
            className="w-full px-3 py-2.5 pl-9 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-blue focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue"
          />
          <Icon
            icon="lucide:search"
            className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
          />
        </div>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className="w-full sm:w-48 h-[42px] px-3 text-sm border border-slate-200 rounded-lg bg-slate-50 text-primary-900 focus:border-brand-blue focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue"
        >
          <option value="">All stages</option>
          {IDEA_FLOW_STAGE_NAMES.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </div>

      <Typography as="p" className="text-sm text-slate-500 -mt-2">
        {filteredQuestions.length} of {questions.length} questions
      </Typography>

      <Table
        columns={[
          {
            header: "ID",
            accessor: "id",
            render: (value) => (
              <Typography as="p" className="text-sm font-mono text-slate-600">
                {value}
              </Typography>
            ),
          },
          {
            header: "Stage",
            accessor: "stage",
            render: (_, record) => {
              const label = getStageLabel(record);
              const stageNumber = record?.stage_number;
              return (
                <div className="flex flex-col">
                  <Typography as="p" className="text-sm font-semibold text-slate-800">
                    {label}
                  </Typography>
                  {stageNumber != null && (
                    <Typography as="p" className="text-xs text-slate-500">
                      Stage {stageNumber}
                    </Typography>
                  )}
                </div>
              );
            },
          },
          {
            header: "Question",
            accessor: "question",
            render: (value) => (
              <Typography as="p" className="text-sm text-slate-800 leading-relaxed max-w-xl">
                {normalizeQuestionText(value)}
              </Typography>
            ),
          },
          {
            header: "Actions",
            accessor: "id",
            render: (_, record) => (
              <button
                type="button"
                onClick={() => openEdit(record)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-violet-700 hover:bg-violet-50"
              >
                <Icon icon="lucide:pencil" className="h-4 w-4" />
                Edit
              </button>
            ),
          },
        ]}
        data={filteredQuestions}
        rowKey="id"
        emptyMessage={
          searchTerm || stageFilter
            ? "No questions match your filters."
            : "No stage questions found."
        }
        className="w-full"
      />

      {editingRecord && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={closeEdit}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-200">
              <Typography as="p" className="text-xs font-semibold uppercase tracking-wide text-violet-600">
                Edit question
              </Typography>
              <Typography as="h3" className="text-lg font-semibold text-slate-900 mt-1">
                {getStageLabel(editingRecord)}
              </Typography>
              <Typography as="p" className="text-xs text-slate-500 mt-1">
                ID {editingRecord.id} · Stage {editingRecord.stage_number}
              </Typography>
            </div>
            <div className="px-6 py-5">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Question text
              </label>
              <textarea
                autoFocus
                rows={5}
                value={editQuestion}
                onChange={(e) => setEditQuestion(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                placeholder="Enter the rubric question..."
              />
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50">
              <Button variant="outline" onClick={closeEdit} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save question"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StageQuestionList;
